import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import ComparedVehicle from './ComparedVehicle';
import CostOfOwnership from '../CostOfOwnership/CostOfOwnership';

import SpeedOfCharge from '../../../components/SpeedOfCharge/SpeedOfCharge';
import ElectricRange from '../../../components/ElectricRange/ElectricRange';
import UserPrefsContext from '../../../context/UserPrefs/UserPrefsContext';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

import './CompareVehicles.scss';

import ComparedCarDetails from './ComparedCarDetails';
import ComparedVehicleToggle from './ComparedVehicleToggle';

import { FormattedMessage, useIntl } from 'react-intl';

const CompareVehicles = ({
  ip,
  uuid,
  props,
  electricVehicles,
  gasolineVehicles,
  zipcode,
}) => {
  const intl = useIntl();
  const userPrefs = React.useContext(UserPrefsContext);

  const safeElectricVehicles = useMemo(
    () => (Array.isArray(electricVehicles) ? electricVehicles : []),
    [electricVehicles]
  );
  const safeGasolineVehicles = useMemo(
    () => (Array.isArray(gasolineVehicles) ? gasolineVehicles : []),
    [gasolineVehicles]
  );

  const [comparedVehicle1, setComparedVehicle1] = useState(null);
  const [comparedVehicle2, setComparedVehicle2] = useState(null);
  const [comparedVehicle3, setComparedVehicle3] = useState(null);
  const [selectedVehicleNumber, setSelectedVehicleNumber] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    document.title = process.env.REACT_APP_PAGES_COMPARE_TITLE;

    if (!isInitialized) {
      const combinedVehicles = [...safeElectricVehicles, ...safeGasolineVehicles];
      const filterHandle = userPrefs.get('vehicleHandleCompareFilter');
      if (filterHandle) {
        const matchedVehicle = combinedVehicles.find(
          (vehicle) => vehicle.handle === filterHandle
        );
        if (matchedVehicle) {
          setComparedVehicle1(matchedVehicle);
        }
      }
      setIsInitialized(true);
    }
  }, [userPrefs, safeElectricVehicles, safeGasolineVehicles, isInitialized]);
  
  if (!electricVehicles || !gasolineVehicles) {
    return (
      <div className="compare-vehicles-loading">
        <LoadingSpinner />
      </div>
    );
  }

  const cars = [comparedVehicle1, comparedVehicle2, comparedVehicle3].filter(
    Boolean
  );

  const filteredGasolineVehicles = safeGasolineVehicles.filter(
    (vehicle) => vehicle.images?.length > 0
  );

  const title = {
    costAnalysis: intl.formatMessage({
      id: 'graph.title.costAnalysis',
      defaultMessage: 'Cost Analysis',
    }),
    speedOfCharge: intl.formatMessage({
      id: 'graph.title.speedOfCharge',
      defaultMessage: 'Speed of Charge',
    }),
    electricRange: intl.formatMessage({
      id: 'graph.title.electricRange',
      defaultMessage: 'Electric Range',
    }),
  };

  const vehicleContainerClasses = [0, 1, 2].map((i) =>
    ['col-sm-4', 'd-sm-block'].concat(
      selectedVehicleNumber !== i ? 'd-none' : ''
    )
  );

  return (
    <div className="CompareVehicles">
      <div className="bgHolder" />
      <section className="container">
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12 text-center">
              <h1 className="evHead">
                <FormattedMessage
                  id="compareVehicles"
                  defaultMessage="Compare new vehicles"
                />
              </h1>
              <p className="lead" style={{ marginTop: '20px' }}>
                <FormattedMessage
                  id="compareVehicles.subTitle"
                  defaultMessage="Pick at least two vehicles to begin the comparison."
                />
              </p>
              <ComparedVehicleToggle
                selectedVehicleNumber={selectedVehicleNumber}
                onChangeVehicleNumber={setSelectedVehicleNumber}
                comparedVehicles={[
                  comparedVehicle1,
                  comparedVehicle2,
                  comparedVehicle3,
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="container compared-vehicle-container">
        <div className="container">
          <div className="row">
            {[comparedVehicle1, comparedVehicle2, comparedVehicle3].map(
              (vehicle, idx) => (
                <div
                  key={idx}
                  className={vehicleContainerClasses[idx].join(' ')}
                >
                  <ComparedVehicle
                    vehicle={vehicle}
                    setVehicle={
                      [
                        setComparedVehicle1,
                        setComparedVehicle2,
                        setComparedVehicle3,
                      ][idx]
                    }
                    electricVehicles={safeElectricVehicles}
                    gasolineVehicles={filteredGasolineVehicles}
                    isPrimary={idx === 0}
                  />
                  {vehicle && <ComparedCarDetails vehicle={vehicle} />}
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {cars.length > 1 && (
        <div className="graph-container">
          <div className="container">
            <CostOfOwnership
              cars={cars}
              title={title.costAnalysis}
              comparison="comparison"
              zipcode={zipcode}
            />
          </div>

          {process.env.REACT_APP_SPEED_OF_CHABGE_GRAPH && (
            <section className="container">
              <div className="container">
                <SpeedOfCharge
                  cars={cars}
                  title={title.speedOfCharge}
                  comparison="comparison"
                />
              </div>
            </section>
          )}

          {process.env.REACT_APP_ELECTRIC_RANGE_GRAPH && (
            <section className="container">
              <div className="container">
                <ElectricRange
                  cars={cars}
                  title={title.electricRange}
                  comparison="comparison"
                />
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

CompareVehicles.propTypes = {
  electricVehicles: PropTypes.array,
  gasolineVehicles: PropTypes.array,
  zipcode: PropTypes.string,
};

export default CompareVehicles;
